import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import {
  ActivateDeactivateCommands,
  CascadeSequenceProperties,
  ComfortEnsuringCommands,
  ComfortEnsuringProperties,
  CommonFlowTemperature,
  ConsolidatedFeature,
  ConsolidatorDevice,
  DcSensorValueProperties,
  EssInverterPowerProperties,
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  HeatingCascadeTemperatureCommonSupplyCommands,
  IsActiveProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  PowerLimitationTooltip,
  VentilationFanProperties,
} from '@vi-iot/client';

import { GTMService } from '@services';
import {
  DevicesStore,
  FeatureService,
  InstallationDetailsStore,
  InstallationFeaturesStore,
} from '@store';

import { take, takeUntil } from 'rxjs/operators';
import { CustomTranslationsService } from '../../../operational-data-common/translations';
import { PillType } from '../../../shared/components/pill/pill.model';
import { InstallationService } from '../../services/installation.service';
import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';
import { InstallationDetailsMappingService } from '../../services/installation-details-mapping.service';

@Component({
  selector: 'app-system',
  templateUrl: './system.component.html',
  styleUrls: ['./system.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemComponent implements OnInit, OnDestroy {
  burner$: Observable<Feature<IsActiveProperty, never>>;
  modulation$: Observable<Feature<IsNumberValueProperty, never>>;
  boilerTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  commonFlowTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  outsideTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  emergencyMode$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  cascadeSequence$: Observable<Feature<CascadeSequenceProperties, never>>;
  // TODO VIG-3076 commented temporarily
  // defrosting$: Observable<Feature<IsActiveProperty>>;
  essSystemType$: Observable<Feature<IsStringValueProperty, never>>;
  ambientTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  powerGridConnectionPoint$: Observable<Feature<IsNumberValueProperty, never>>;
  inverterPower$: Observable<Feature<EssInverterPowerProperties, unknown>>;
  inverterPowerConsolidated$: Observable<Feature<ConsolidatedFeature, unknown>>;
  inverterPowerConsolidatedFeatures$: Observable<Feature<ConsolidatedFeature, unknown>>;
  inverterPowerConsolidatedPerDevice$: Observable<ConsolidatorDevice>;
  isMultiFamily$: Observable<Feature<IsActiveProperty, never>>;
  comfortEnsuringMode$: Observable<Feature<ComfortEnsuringProperties, ComfortEnsuringCommands>>;
  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  leadDeviceCommonSupply$: Observable<
    Feature<CommonFlowTemperature, HeatingCascadeTemperatureCommonSupplyCommands>
  >;
  solarStatus$: Observable<Feature<IsStringValueProperty, never>>;
  heatingDeviceStatus$: Observable<Feature<IsStringValueProperty, never>>;
  configurationFuelCapacity$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingBoilerAshLevel$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingDeviceFuelCurrent$: Observable<Feature<IsNumberValueProperty, never>>;
  fuelVolume$: Observable<number>;
  ventilationFanExhaust$: Observable<Feature<VentilationFanProperties, never>>;
  ventilationFanSupply$: Observable<Feature<VentilationFanProperties, never>>;
  ventilationHeatRecovery$: Observable<Feature<IsNumberValueProperty, never>>;
  deviceId: string;
  inverterPowerConsolidatedDeviceId: string;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly pillType = PillType;
  powerLimitationLabel$: Observable<PowerLimitationTooltip[]>;
  constructor(
    public installationService: InstallationService,
    public gtmService: GTMService,
    public installationDetailsStore: InstallationDetailsStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    public devicesStore: DevicesStore,
    protected customTranslationsService: CustomTranslationsService,
    private featureService: FeatureService,
    private installationDetailsMappingService: InstallationDetailsMappingService
  ) {}

  ngOnInit(): void {
    this.featureService
      .getConsolidatedFeatures(FeatureNames.inverterPowerConsolidated)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((feature) => {
        this.inverterPowerConsolidatedDeviceId = feature?.deviceId;
      });
    this.devicesStore.getSelectedDevice$
      .pipe(take(1), takeUntil(this.ngUnsubscribe))
      .subscribe((richDevice) => {
        this.deviceId = richDevice?.id ?? '';
      });
    this.burner$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.burnerStatus
    );
    this.modulation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.burnerModulation
    );
    this.boilerTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.boilerTemperature
    );
    this.commonFlowTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.boilerCommonSupplyTemperature
    );
    this.outsideTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.outsideTemperature
    );

    this.emergencyMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceEmergencyOperation
    );
    // TODO VIG-3076 commented temporarily
    // this.defrosting$ = this.installationFeaturesStore.selectInstallationDeviceFeature(FeatureNames.outdoorDefrosting);
    this.essSystemType$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essSystemType
    );
    this.ambientTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essAmbientTemperature
    );
    this.powerGridConnectionPoint$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essGridTransferPowerExchange
    );

    this.inverterPower$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.inverterPower,
      this.deviceId
    );

    this.inverterPowerConsolidated$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.inverterPowerConsolidated,
      this.deviceId
    );

    this.inverterPowerConsolidatedFeatures$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.inverterPowerConsolidated
    );

    this.inverterPowerConsolidatedPerDevice$ = this.installationFeaturesStore.selectConsolidatedValues(
      FeatureNames.inverterPowerConsolidated,
      this.deviceId,
      this.inverterPowerConsolidatedDeviceId
    );

    this.installationService.subscribeToDefrostingFeature();

    this.isMultiFamily$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.configurationMultiFamilyHouse
    );

    this.comfortEnsuringMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceComfortEnsuring
    );
    this.leadDeviceCommonSupply$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.cascadeHeatingCommonSupply
    );
    this.heatingDeviceStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceStatus
    );
    this.solarStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.solarStatus
    );
    this.heatingDeviceFuelCurrent$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceFuelCurrent
    );
    this.fuelVolume$ = this.installationFeaturesStore.getFuelVolume$;
    this.heatingBoilerAshLevel$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingBoilerAshLevel
    );

    this.ventilationFanExhaust$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationFanExhaust
    );
    this.ventilationFanSupply$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationFanSupply
    );
    this.ventilationHeatRecovery$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationHeatRecovery
    );
    this.powerLimitationLabel$ = this.installationDetailsMappingService.powerLimitationLabel(
      this.deviceId
    );
  }

  activateEmergencyMode(): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.heatingDeviceEmergencyOperation,
      true,
      'active',
      '',
      FeatureCommands.activate
    );
    this.gtmService.track('onConfirm', 'Emergency mode', 'system');
  }

  cancelEmergencyModeConfirmation(): void {
    this.gtmService.track('onCancel', 'Emergency mode confirmation', 'system');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
